import { yupResolver } from '@hookform/resolvers/yup';
import axios from 'axios';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';

import {
  CHANNEL_DESCRIPTION_MAX_LENGTH,
  CHANNEL_LOGO_SIZE_LIMIT,
  CHANNEL_LOGO_SUPPORTED_FORMATS,
  CHANNEL_NAME_MAX_LENGTH,
  getChannelURL
} from 'utils';
import { channelsActions, profileActions } from 'store/actions';
import { channelByIdSelector } from 'store/selectors';
import { api, LoggerService, tracker } from 'services';
import { useRouter, useStore, useToast } from 'hooks';
import { ImageUpload } from 'containers';
import { Input, Loader, TextArea } from 'components';

import styles from './EditChannelPanel.module.scss';

export type EditChannelPanelProps = {
  channelId: string;
  onSubmitted?(): void;
};

const validationSchema = yup.object().shape({
  name: yup
    .string()
    .max(CHANNEL_NAME_MAX_LENGTH, `must be less than ${CHANNEL_NAME_MAX_LENGTH} characters`)
    .matches(/^[a-zA-Z0-9_]+$/, 'Letters, numbers and _ allowed')
    .required('channel name is required'),
  description: yup
    .string()
    .max(CHANNEL_DESCRIPTION_MAX_LENGTH, `must be less than ${CHANNEL_DESCRIPTION_MAX_LENGTH} characters`),
  logo: yup
    .mixed()
    .test('fileSize', 'The file is too large', (file: File) => !file || file.size <= CHANNEL_LOGO_SIZE_LIMIT)
    .test(
      'fileFormat',
      'File format is not supported',
      (file: File) => !file || CHANNEL_LOGO_SUPPORTED_FORMATS.map(ext => `image/${ext}`).includes(file?.type)
    )
});

const EditChannelPanel = ({ channelId, onSubmitted }: EditChannelPanelProps): JSX.Element => {
  const dispatch = useDispatch();
  const router = useRouter();
  const toast = useToast();
  const { channel } = useStore(state => ({
    channel: channelByIdSelector(channelId)(state)
  }));

  const form = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      name: channel?.name || '',
      description: channel?.description || '',
      logo: null
    }
  });

  return (
    <div className={styles.container}>
      {form.formState.isSubmitting && <Loader />}

      <FormProvider {...form}>
        <form
          onSubmit={form.handleSubmit(async validValues => {
            try {
              tracker.editChannelDetails(channelId);
              const { data: updatedChannel } = await api.requests.ltv.updateChannel({
                name: validValues.name,
                description: validValues.description,
                logo: validValues.logo || undefined
              });

              tracker.editChannelDetailsSuccess(channelId);
              dispatch(channelsActions.setChannel(updatedChannel));
              dispatch(profileActions.setChannelName({ channelName: updatedChannel.name }));

              toast.success('Channel details updated', { id: 'UPDATE_CHANNEL_INFO' });

              if (channel?.name !== updatedChannel.name) {
                router.replace(getChannelURL(updatedChannel.name));
              }

              onSubmitted?.();
            } catch (error) {
              LoggerService.log('SUBMIT_EDIT_CHANNEL_FORM', error);

              if (error instanceof Error) {
                tracker.editChannelDetailsFail(channelId, error);
              }

              if (axios.isAxiosError(error)) {
                switch (error.response?.status) {
                  case 400: {
                    form.setError('name', { type: 'server', message: 'Channel name is already taken' });

                    toast.error('Channel name is already taken', { id: 'UPDATE_CHANNEL_INFO' });
                    break;
                  }

                  default: {
                    toast.error(error.response?.data.message, { id: 'UPDATE_CHANNEL_INFO' });
                  }
                }
              } else if (error instanceof Error) {
                toast.error(error.message, { id: 'UPDATE_CHANNEL_INFO' });
              }
            }
          })}
        >
          <Controller
            name="logo"
            control={form.control}
            render={({ field: { value, onChange } }) => (
              <ImageUpload
                actions={['update']}
                defaultValue={channel?.logo}
                maxSize={CHANNEL_LOGO_SIZE_LIMIT}
                className={styles.logo}
                invalid={!!form.formState.errors.logo}
                feedback={form.formState.errors.logo?.message}
                value={value}
                onChange={onChange}
              />
            )}
          />

          <Controller
            name="name"
            control={form.control}
            render={({ field }) => (
              <Input
                label="Channel name"
                className={styles.name}
                before="livetalks.tv/"
                invalid={!!form.formState.errors.name}
                placeholder="your channel"
                feedback={form.formState.errors.name?.message}
                {...field}
              />
            )}
          />

          <Controller
            name="description"
            control={form.control}
            render={({ field }) => (
              <TextArea
                label="Description"
                className={styles.description}
                maxLength={CHANNEL_DESCRIPTION_MAX_LENGTH}
                placeholder="Add description"
                {...field}
              />
            )}
          />

          <button type="submit">Save changes</button>
        </form>
      </FormProvider>
    </div>
  );
};

export default EditChannelPanel;
